import React from "react";
import { Button, Col, Input, Label, Row } from "reactstrap";

/**
 * Content-mode Array:text building block (SCRUM-643) — the update screens'
 * multi-text-row editor with the count badge and icon add/delete buttons,
 * extracted verbatim from HandleContent (both statuses) (the SCRUM-640/687
 * characterization drives the buttons via their .ni-fat-add / .fa-trash
 * icons). The create wizard uses the shared ArrayTextField instead — a
 * different UI since SCRUM-648 Decision 1 (commit b37b79e6).
 *
 * Emits the full string[] via `onChange(name, value)`. Whether an edit
 * reaches the parent is the HOST's business: HandleContent (required) forwards
 * it via onVariableChange, HandleContent (optional) keeps it local (pinned
 * silent quirk).
 *
 * Preserved quirks (do NOT clean up):
 * - The host's scope filter normalizes the value, so `[]` renders ZERO rows
 *   with the delete button ENABLED (disabled only at exactly one row); the
 *   length guard lives in the delete handler.
 * - The row count is a disabled facebook-styled button.
 */
export type ContentArrayTextFieldProps = {
  settingVar: { variable?: string; value?: unknown };
  onChange: (name: string | undefined, value: string[]) => void;
};

export default function ContentArrayTextField({ settingVar, onChange }: ContentArrayTextFieldProps) {
  const rows: string[] = Array.isArray(settingVar.value) ? settingVar.value : [];

  const addRow = () => onChange(settingVar.variable, [...rows, ""]);

  const deleteRow = () => {
    if (rows.length > 1) {
      onChange(settingVar.variable, rows.slice(0, -1));
    }
  };

  const updateRow = (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(
      settingVar.variable,
      rows.map((item, i) => (i === idx ? newValue : item))
    );
  };

  return (
    <div>
      <Label className="form-control-label" md="2">
        {settingVar.variable}
      </Label>
      <div className="text-center mb-3">
        <Button className="btn-icon-only rounded-circle" color="facebook" disabled>
          {rows.length}
        </Button>
        <Button
          className="btn-slack btn-icon-only rounded-circle"
          color="default"
          onClick={addRow}
        >
          <i className="ni ni-fat-add" />
        </Button>
        <Button
          className="btn-icon-only rounded-circle"
          color="pinterest"
          onClick={deleteRow}
          disabled={rows.length === 1}
        >
          <i className="fas fa-trash" />
        </Button>
      </div>
      <div>
        {rows.map((value, idx) => (
          // index key: controlled row, no row-local state (SCRUM-2183)
          <Row key={idx}>
            <Col>
              <Label className="form-control-label">Text {idx + 1}</Label>
            </Col>
            <Col>
              <Input
                className="form-control-sm"
                placeholder={`Enter text ${idx + 1}`}
                type="text"
                value={value}
                onChange={updateRow(idx)}
              />
            </Col>
          </Row>
        ))}
      </div>
    </div>
  );
}

// renders its own variable-name label — the dispatcher skips its label row
ContentArrayTextField.ownsChrome = true;
